import type { Editor } from '@tiptap/react';
import { DOMSerializer } from '@tiptap/pm/model';

/**
 * Serialize a document range [from, to] to HTML so AI actions send the original MARKUP
 * (headings/bold/lists) to the model — translate's prompt can only preserve formatting it
 * receives. textBetween() flattens it to plain text. Falls back to plain text on any failure
 * (never break the action).
 */
export function rangeToHtml(editor: Editor, from: number, to: number): string {
  try {
    const slice = editor.state.doc.slice(from, to);
    const fragment = DOMSerializer.fromSchema(editor.schema).serializeFragment(slice.content);
    const div = document.createElement('div');
    div.appendChild(fragment);
    return div.innerHTML;
  } catch {
    return editor.state.doc.textBetween(from, to, '\n');
  }
}

/**
 * Resolve the top-level block at `pos` and return its start position and node.
 *
 * The drag handle's onNodeChange reports the NODE position — the position OF the
 * block (0 for the first block, etc.) — so `doc.nodeAt(pos)` yields the block
 * directly and `pos` is its start. As a fallback (e.g. a caret position passed
 * from elsewhere), resolve `pos` to its depth-1 ancestor. Returns null if no
 * block can be found.
 */
export function blockAt(editor: Editor, pos: number) {
  const { doc } = editor.state;
  const clamped = Math.min(Math.max(pos, 0), doc.content.size);

  // Node position: the block sits AT this position.
  const nodeAtPos = doc.nodeAt(clamped);
  if (nodeAtPos && nodeAtPos.isBlock) {
    return { start: clamped, node: nodeAtPos };
  }

  // Fallback: an inside/caret position — walk up to the top-level block.
  const $pos = doc.resolve(clamped);
  if ($pos.depth >= 1) {
    return { start: $pos.before(1), node: $pos.node(1) };
  }
  return null;
}

/** Delete the top-level block containing `pos`. */
export function deleteBlockAt(editor: Editor, pos: number): boolean {
  const block = blockAt(editor, pos);
  if (!block) return false;
  return editor
    .chain()
    .focus()
    .deleteRange({ from: block.start, to: block.start + block.node.nodeSize })
    .run();
}

/** Insert a copy of the top-level block containing `pos` immediately after it. */
export function duplicateBlockAt(editor: Editor, pos: number): boolean {
  const block = blockAt(editor, pos);
  if (!block) return false;
  const after = block.start + block.node.nodeSize;
  return editor.chain().focus().insertContentAt(after, block.node.toJSON()).run();
}
